import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { socialLinks } from '../config/socialLinks';

const StructuredData = () => {
  const localBusinessData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Hawkins Carpet Cleaning",
    "image": "https://hawkinscarpet.com/logo.png",
    "url": "https://hawkinscarpet.com",
    "telephone": "+12702555555",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "715 Lafeyette Drive",
      "addressLocality": "Owensboro",
      "addressRegion": "KY",
      "postalCode": "42303",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "37.7574",
      "longitude": "-87.1167"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "08:00",
      "closes": "17:00"
    },
    "sameAs": [
      socialLinks.facebook,
      socialLinks.youtube,
      socialLinks.instagram,
      socialLinks.tiktok
    ]
  }), []);

  const serviceData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Carpet Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Hawkins Carpet Cleaning"
    },
    "areaServed": [
      {
        "@type": "County",
        "name": "Daviess",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "KY"
        }
      },
      {
        "@type": "County",
        "name": "Hancock",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "KY"
        }
      },
      {
        "@type": "County",
        "name": "Henderson",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "KY"
        }
      },
      {
        "@type": "County",
        "name": "McLean",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "KY"
        }
      },
      {
        "@type": "County",
        "name": "Ohio",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "KY"
        }
      },
      {
        "@type": "County",
        "name": "Perry",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "IN"
        }
      },
      {
        "@type": "County",
        "name": "Spencer",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "IN"
        }
      },
      {
        "@type": "County",
        "name": "Warrick",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "IN"
        }
      },
      {
        "@type": "County",
        "name": "Vanderburgh",
        "address": {
          "@type": "PostalAddress",
          "addressRegion": "IN"
        }
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Carpet Cleaning Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Residential Carpet Cleaning"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Commercial Carpet Cleaning"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Stain Removal"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pet Odor Treatment"
          }
        }
      ]
    }
  }), []);

  const faqData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What services does Hawkins Carpet Cleaning offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "At Hawkins Carpet Cleaning, we specialize in low-moisture carpet cleaning, upholstery cleaning, stain removal, and pet odor treatment. Our eco-friendly cleaning methods leave your carpets fresh, clean, and dry in as little as 90 minutes!"
        }
      },
      {
        "@type": "Question",
        "name": "How do I schedule an appointment?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Booking your carpet cleaning service is easy! You can call us directly or use our convenient online booking system on our website: HawkinsCarpet.com."
        }
      },
      {
        "@type": "Question",
        "name": "Are your cleaning products safe for children and pets?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! We use non-toxic, eco-friendly cleaning solutions that are safe for children, pets, and allergy-sensitive individuals. Your family's health and safety are our top priorities."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer any discounts or promotions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We frequently run seasonal promotions and special offers. Follow us on social media and check our website for the latest deals. Don't miss out on savings!"
        }
      },
      {
        "@type": "Question",
        "name": "How long does carpet cleaning take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our low-moisture cleaning method ensures fast and efficient service. Most jobs take 1 to 2 hours, and carpets dry in just 90 minutes, so you can get back to enjoying your space in no time."
        }
      },
      {
        "@type": "Question",
        "name": "Can you remove tough stains like wine, coffee, or pet accidents?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Our advanced enzyme-based stain removal solutions effectively break down tough stains, including wine, coffee, pet accidents, and more. We'll restore your carpets to their best condition!"
        }
      },
      {
        "@type": "Question",
        "name": "What areas do you serve?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We proudly serve Daviess and the surrounding counties. Contact us to confirm if we cover your location—we'd love to help refresh your home!"
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer emergency or same-day cleaning services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We understand accidents happen, which is why we offer emergency carpet cleaning services when available. Call us as soon as possible, and we'll do our best to accommodate you."
        }
      },
      {
        "@type": "Question",
        "name": "What payment methods do you accept?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We accept cash, credit/debit cards, and mobile payment options for your convenience. Our goal is to make the process smooth and hassle-free."
        }
      }
    ]
  }), []);

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqData)}
      </script>
    </Helmet>
  );
};

export default React.memo(StructuredData); 